'use client';

import { twMerge } from '@shared/themes';
import { useTranslations } from 'next-intl';
import { useLayoutEffect, useRef, useState } from 'react';
import { Smile } from '../svg';
import { RecentSearches } from './components/RecentSearches/RecentSearches';
import { SearchInput } from './components/SearchInput/SearchInput';
import { SearchResults } from './components/SearchResults/SearchResults';
import { useRecentSearches } from './hooks/useRecentSearches';
import { useSearch } from './hooks/useSearch';
import { useSearchResultNavigation } from './hooks/useSearchResultNavigation';
import type { SearchProps, SearchResult } from './types';

/**
 * SearchContainer is a wrapper component that provides a complete search experience with dynamic content visibility.
 * It acts as a container that wraps your content and intelligently shows/hides it based on the search state,
 * while providing full search functionality including recent searches, results, and empty states.
 *
 * The component functions as a smart wrapper around your content. When the search input is not focused and there's
 * no active search, it displays the `children` content. As the user interacts with the search, the component
 * dynamically hides the children and shows relevant search UI (recent searches, results, or empty states).
 *
 * ## Variants
 *
 * ### Fullscreen Variant (default)
 * - Uses `ll-grid-container` CSS class internally, which provides a responsive grid layout system.
 * - Automatically calculates and sets `minHeight` to fill the viewport height (`calc(100vh - topOffset)`)
 * - Updates height dynamically on window resize
 *
 * ### Dialog Variant
 * - Does not use `ll-grid-container` or height calculations
 * - Renders search input and content in a scrollable container
 * - Optimized for use within modal/dialog components
 *
 * @param children - React nodes to render when search is inactive. These will be hidden when the user focuses
 *                   the search input or performs a search.
 * @param placeholder - The placeholder text displayed in the search input
 * @param searchType - The type of search to perform.
 * @param vertical - The vertical of the search to perform.
 * @param className - Additional CSS classes to apply to the search input component
 * @param variant - The layout variant of the search component.
 * @param onCancel - Optional callback function called when the search is cancelled (via cancel button or result selection)
 */
function SearchContainer({ children, placeholder, searchType, vertical = 'home', className, variant = 'fullscreen', onCancel }: SearchProps) {
  const t = useTranslations('general.search');
  const [isSearchFocus, setIsSearchFocus] = useState(false);
  const { searchTerm, setSearchTerm, results, debouncedSearch, isSearchActive } = useSearch({ searchType });
  const { navigateToResult } = useSearchResultNavigation(searchType);
  const { searches, addRecentSearch } = useRecentSearches(vertical);
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (variant === 'dialog') return;
    const updateMinHeight = () => {
      if (containerRef.current) {
        const topOffset = containerRef.current.getBoundingClientRect().top;
        containerRef.current.style.minHeight = `calc(100vh - ${topOffset}px)`;
      }
    };

    updateMinHeight();
    window.addEventListener('resize', updateMinHeight);

    return () => window.removeEventListener('resize', updateMinHeight);
  }, [variant]);

  const handleFocus = () => {
    setIsSearchFocus(true);
  };

  const handleCancel = () => {
    setIsSearchFocus(false);
    setSearchTerm('');
    onCancel?.();
  };

  const handleResultClick = (result: SearchResult) => {
    addRecentSearch(debouncedSearch);
    navigateToResult(result, handleCancel);
  };

  const searchInput = (
    <SearchInput
      value={searchTerm}
      placeholder={placeholder}
      onFocus={handleFocus}
      onChange={setSearchTerm}
      onCancel={handleCancel}
      className={twMerge('mb-5', className)}
      showCancel={isSearchFocus}
    />
  );

  const renderContent = () => (
    <>
      {!isSearchFocus && debouncedSearch.length === 0 && children}

      {isSearchFocus && !isSearchActive ? (
        searches.length > 0 && debouncedSearch.length === 0 ? (
          <div className="ll-container-inner mb-8 ml-5.5" data-track-container="Recent Searches">
            <RecentSearches searches={searches} onSearchClick={setSearchTerm} />
          </div>
        ) : (
          <div className="ll-container-inner flex justify-center">
            <div className="flex flex-col items-center gap-4 my-18">
              <p>{t('find_game', { searchType })}</p>
              <Smile />
            </div>
          </div>
        )
      ) : null}

      {isSearchActive ? (
        results.length > 0 ? (
          <SearchResults searchType={searchType} results={results} searchTerm={debouncedSearch} onResultClick={handleResultClick} className="ll-container-inner px-5.5" />
        ) : (
          <div className="ll-container-inner flex justify-center mt-8 border-b border-border" data-track-name="No Results">
            <p className="mb-8 text-foreground-subtle">{t('no_results', { searchTerm: debouncedSearch })}</p>
          </div>
        )
      ) : null}
    </>
  );

  if (variant === 'dialog') {
    return (
      <div data-testid="search-container" className="flex flex-col h-full min-h-0">
        {searchInput}
        <div className="overflow-y-auto hide-scrollbar" data-track-container="Searches">
          {renderContent()}
        </div>
      </div>
    );
  }

  return (
    <div role="search" ref={containerRef} data-testid="search-container">
      <div className="ll-grid-container" data-track-container="Searches">
        <div className="ll-container-inner">{searchInput}</div>
        {renderContent()}
      </div>
    </div>
  );
}

export { SearchContainer };
